/**
 * Registry PARCIAL do módulo `ai-framework` — spec-001 do
 * `project-plataforma-por-chat`, [T018] / US-2.
 *
 * Uma entrada só: o cartão da ação que o agente PROPÔS e não executou. No
 * ia_framework a escrita nunca roda no turno (RF6.1) — o motor registra a
 * proposta, marca o run como `awaiting_approval` e devolve o resumo; quem
 * decide é a fila do gestor (spec-004). Por isso o cartão é 100% informativo,
 * sem botão nenhum.
 *
 * ## Por que a chave NÃO entra no `LEGACY_TOOL_TO_UI`
 *
 * O mapa legado casa por NOME DA TOOL, e o agente do ia_framework chama as
 * MESMAS write-tools do IA CRM (`create_tarefa`, `create_oportunidade`…) — o
 * catálogo dele é o `ToolRegistry` do `aawz_intelligence_v2`. Mapear
 * `create_tarefa → ai-framework/acao-proposta` faria o chat do CRM desenhar o
 * cartão do parceiro (e vice-versa, quando o [T017] mapear as write-tools
 * genéricas para `crm/confirmacao`): duas superfícies disputando a mesma chave.
 *
 * Quem escolhe este cartão é a SUPERFÍCIE, no `APIAgentChat`: lá a proposta
 * nasce com `ui.component` explícito (`apiAgentChat.js`), e `ui.component` tem
 * precedência sobre o mapa no `chat-message.vue`. É a mesma regra do contrato —
 * "o backend sabe melhor que a tabela" —, com a diferença de que aqui quem sabe
 * é o cliente da rota, porque o agent-chat ainda não emite `ui` no fio.
 */
import { FEATURES } from '@constants/Text'

/** A chave do cartão no `UI_REGISTRY` (`contracts/ui-components.md` §3). */
export const CHAVE_ACAO_PROPOSTA = 'ai-framework/acao-proposta'

/**
 * Chave do módulo no catálogo de navegação — `ROUTE_KEYS.AI_FRAMEWORK` de
 * `@constants/navigation-routes`.
 *
 * Redigitada aqui, e não importada: `navigation-routes.js` puxa constantes de
 * uma dúzia de módulos (back-office, finia, tesouraria, comissões…), e este
 * arquivo é carregado pelo agregador do registry, que por sua vez é importado
 * pela bolha do chat. Puxar aquele grafo para dentro do renderer é caro e abre
 * espaço para ciclo — o mesmo tipo de ciclo que já quebrou o `extends APIChat`
 * do fee-fixo (ver `utils/contexto-de-tela.js`).
 *
 * ⚠️ Cópia literal é como um rename silencioso passa. O `registry.unit.js`
 * importa `ROUTE_KEYS` e assere a igualdade — o dia em que a chave mudar lá, o
 * teste fica vermelho aqui.
 */
export const MODULO_DA_NAVEGACAO = 'ai-framework'

/**
 * O único estado em que este cartão sabe desenhar.
 *
 * A parte nasce JÁ resolvida (a proposta é o desfecho da tool: no ia_framework
 * a escrita não executa), e a mensagem recarregada a redesenha em
 * `output-available` (`chat-message.vue`, `partesDeToolCalls`). Declarar
 * `input-available` aqui seria mentira: o cartão não tem esqueleto — ele exige
 * `operacao`/`entidade`/`resumo`, e montá-lo sem eles produziria warning de
 * prop no meio da conversa.
 */
const ESTADOS_DO_CARTAO = ['output-available']

export default {
  [CHAVE_ACAO_PROPOSTA]: {
    // Lazy nos dois: o componente e o schema só saem do bundle quando um
    // cartão deste tipo aparece de fato numa conversa.
    component: () =>
      import(
        '@modules/ai-framework/components/proposed-action-card/proposed-action-card.vue'
      ),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-ai-framework-acao-proposta.schema.json'
      ),
    module: MODULO_DA_NAVEGACAO,
    states: ESTADOS_DO_CARTAO,
    label: 'Ação proposta',
    // Flag-mestra do módulo. O registry é fail-closed: sem ela no
    // `parameters/show`, o cartão não monta — dado de módulo não contratado
    // dentro da conversa não tem desfazer.
    feature: FEATURES.AI_FRAMEWORK,
  },
}
